import { Avatar, Symbol, Wordmark } from "@/components/fornada/brand";
import { initials } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth.provider";
import { BarChart3, Home, LogOut, Receipt, User, Users } from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

type Tab = { to: string; label: string; icon: typeof Home; end?: boolean; admin?: boolean };

const TABS: Tab[] = [
  { to: "/", label: "Início", icon: Home, end: true },
  { to: "/orders", label: "Pedidos", icon: Receipt },
  { to: "/campaign", label: "Campanha", icon: BarChart3, admin: true },
  { to: "/sellers", label: "Vendedores", icon: Users, admin: true },
];

/** Telas de pilha (novo pedido, editar campanha) escondem a navegação no mobile. */
const STACK_ROUTES = ["/orders/new", "/campaign/edit"];

export function AppLayout() {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const isAdmin = user?.role === "ADMIN";
  const tabs = TABS.filter((t) => !t.admin || isAdmin);
  const isStack = STACK_ROUTES.includes(pathname);

  return (
    <div className="flex min-h-screen w-full flex-col">
      {/* ≥ 960px: navegação no topo */}
      <header className="sticky top-0 z-10 hidden h-16 shrink-0 items-center gap-8 border-b border-fx-line-subtle bg-fx-bar px-8 backdrop-blur-md min-[960px]:flex">
        <NavLink to="/" aria-label="Fornada — início">
          <Wordmark />
        </NavLink>
        <nav className="flex h-full gap-1">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center border-b-2 px-3.5 text-sm font-semibold transition-colors duration-[180ms] ease-fx",
                  isActive
                    ? "border-fx-cream text-fx-title"
                    : "border-transparent text-fx-faint hover:text-fx-body",
                )
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex-1" />
        <div className="flex items-center gap-2.5">
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-[13px] font-semibold text-fx-title">{user?.name}</span>
            <span className="text-[9.5px] font-bold tracking-[1.3px] text-fx-muted">
              {isAdmin ? "ADMIN" : "VENDEDOR"}
            </span>
          </div>
          <Avatar initials={initials(user?.name)} />
          <button
            type="button"
            onClick={() => signOut()}
            aria-label="Sair"
            className="flex size-11 items-center justify-center rounded-md text-fx-faint transition-colors hover:text-fx-body"
          >
            <LogOut size={18} strokeWidth={1.6} />
          </button>
        </div>
      </header>

      {/* < 960px: app bar */}
      {!isStack && (
        <header className="sticky top-0 z-10 flex h-[60px] shrink-0 items-center gap-2.5 border-b border-fx-line-subtle bg-fx-bar px-5 backdrop-blur-md min-[960px]:hidden">
          <Symbol size={26} />
          <span className="fx-display flex-1 text-[19px] tracking-[-0.02em]">FORNADA</span>
          <Avatar initials={initials(user?.name)} />
        </header>
      )}

      <main
        className={cn(
          "mx-auto w-full max-w-[1120px] flex-1 px-5 pt-6 min-[960px]:px-8 min-[960px]:pt-9",
          isStack ? "pb-32" : pathname === "/campaign" ? "pb-44" : "pb-28",
          "min-[960px]:pb-16",
        )}
      >
        <Outlet />
      </main>

      {/* < 960px: tab bar */}
      {!isStack && (
        <nav
          aria-label="Principal"
          className="fixed inset-x-0 bottom-0 z-10 flex h-[60px] items-stretch border-t border-fx-line-subtle bg-fx-bar pb-[env(safe-area-inset-bottom)] backdrop-blur-md min-[960px]:hidden"
        >
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                cn(
                  "relative flex flex-1 flex-col items-center justify-center gap-1 text-[10.5px] font-semibold transition-colors duration-[180ms] ease-fx",
                  isActive ? "text-fx-title" : "text-fx-faint",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute top-0 h-0.5 w-7 rounded-full bg-fx-cream" />
                  )}
                  <t.icon
                    size={21}
                    strokeWidth={isActive ? 2 : 1.6}
                    className={isActive ? "text-fx-red-icon" : undefined}
                  />
                  {t.label}
                </>
              )}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => signOut()}
            className="flex flex-1 flex-col items-center justify-center gap-1 text-[10.5px] font-semibold text-fx-faint"
          >
            <User size={21} strokeWidth={1.6} />
            Sair
          </button>
        </nav>
      )}
    </div>
  );
}

/** Barra fixa no rodapé das telas de pilha (substitui a TabBar no mobile). */
export function ActionBar({
  children,
  className,
  aboveTabBar,
}: {
  children: React.ReactNode;
  className?: string;
  /** Em telas de aba (ex.: Campanha) a barra de ação fica acima da TabBar. */
  aboveTabBar?: boolean;
}) {
  return (
    <div
      className={cn(
        "fixed inset-x-0 z-10 flex items-center gap-3 border-t border-fx-line-subtle bg-fx-sunken px-5 py-3.5 min-[960px]:static min-[960px]:border-0 min-[960px]:bg-transparent min-[960px]:p-0",
        aboveTabBar
          ? "bottom-[calc(60px+env(safe-area-inset-bottom))]"
          : "bottom-0 pb-[max(14px,env(safe-area-inset-bottom))]",
        className,
      )}
    >
      {children}
    </div>
  );
}
